const competitorCounts = (competitor = {}) =>
  Object.values(competitor).map((v) => Number(v) || 0);

const competitorBenchmark = (competitor = {}) => {
  const counts = competitorCounts(competitor);
  if (!counts.length) return null;
  if (counts.length === 1) return counts[0];
  return Math.round(counts.reduce((sum, n) => sum + n, 0) / counts.length);
};

/** Standard compare labels (vs. = versus; avg. = average per editorial style). */
const COMPARE_LABEL_SINGLE = 'vs. competitor';
const COMPARE_LABEL_AVG = 'vs. competitor avg.';

/** @returns {{ label: string, benchmark: number } | null} */
const getCompetitorCompareNote = (competitor = {}) => {
  const benchmark = competitorBenchmark(competitor);
  if (benchmark == null) return null;
  const counts = competitorCounts(competitor);
  return {
    label: counts.length > 1 ? COMPARE_LABEL_AVG : COMPARE_LABEL_SINGLE,
    benchmark,
  };
};

const formatComparePercent = (subject, benchmark, higherIsBetter = true) => {
  if (benchmark === 0) {
    if (subject === 0) return '0%';
    const ahead = higherIsBetter ? subject > 0 : subject < benchmark;
    return ahead ? '+100%' : '-100%';
  }
  const signedPct = higherIsBetter
    ? Math.round(((subject - benchmark) / benchmark) * 100)
    : Math.round(((benchmark - subject) / benchmark) * 100);
  if (signedPct === 0) return '0%';
  return signedPct > 0 ? `+${signedPct}%` : `${signedPct}%`;
};

/** @returns {{ status: 'better' | 'worse', percentLabel: string } | null} */
const buildCompareChip = (our, competitor, { higherIsBetter = true } = {}) => {
  const benchmark = competitorBenchmark(competitor);
  if (benchmark == null) return null;
  const subject = Number(our) || 0;
  if (subject === benchmark) return null;
  const favorableDelta = higherIsBetter ? subject - benchmark : benchmark - subject;
  if (favorableDelta === 0) return null;
  const status = favorableDelta > 0 ? 'better' : 'worse';
  return {
    status,
    percentLabel: formatComparePercent(subject, benchmark, higherIsBetter),
  };
};

const buildCompetitorKpiCard = ({
  title,
  our,
  competitor,
  subtitleSuffix = '',
  subtitleSuffixClassName,
  higherIsBetter = true,
}) => {
  const compareNote = getCompetitorCompareNote(competitor);
  return {
    title,
    value: String(our),
    subtitle: compareNote ? '' : 'No competitor filings in scope',
    compareNote,
    subtitleSuffix: subtitleSuffix || undefined,
    subtitleSuffixClassName,
    compareChip: buildCompareChip(our, competitor, { higherIsBetter }),
  };
};

/**
 * Map POST /filings/insights kpiCards item to KPICard props (legacy or our/competitor shape).
 * @param {Record<string, unknown>} kpi
 * @returns {{ title: string, value?: string, subtitle?: string, compareNote?: { label: string, benchmark: number }, subtitleSuffix?: string, productFocus?: object, compareChip?: { status: 'better' | 'worse', percentLabel: string } | null }}
 */
export function mapInsightsKpiToCardProps(kpi) {
  const rawTitle = String(kpi?.title || '');
  const title = rawTitle === 'TOTAL FILINGS' ? 'OUR TOTAL FILINGS' : rawTitle;

  if (kpi?.value !== undefined || kpi?.subtitle !== undefined) {
    return {
      title,
      value: kpi.value != null ? String(kpi.value) : '—',
      subtitle: kpi.subtitle != null ? String(kpi.subtitle) : '',
      productFocus: kpi.productFocus,
    };
  }

  const our = kpi?.our ?? 0;
  const competitor = kpi?.competitor && typeof kpi.competitor === 'object' ? kpi.competitor : {};

  if (rawTitle === 'TOP PRODUCT FOCUS') {
    const productName = kpi.product_name ? String(kpi.product_name).trim() : '';
    return buildCompetitorKpiCard({
      title,
      our,
      competitor,
      subtitleSuffix: productName,
      subtitleSuffixClassName: 'kpi-product-name',
    });
  }

  if (kpi.higherIsBetter === false) {
    return buildCompetitorKpiCard({
      title,
      our,
      competitor,
      subtitleSuffix: kpi.subtitleSuffix ? String(kpi.subtitleSuffix) : '',
      subtitleSuffixClassName: kpi.subtitleSuffixClassName,
      higherIsBetter: false,
    });
  }

  if (rawTitle === 'TOP-10 STATE GAPS') {
    const states = Array.isArray(kpi.states) ? kpi.states : [];
    const stateLine = states.length ? states.join(' · ') : 'No state gaps in top 10';
    const counts = competitorCounts(competitor);
    if (counts.length) {
      return buildCompetitorKpiCard({
        title,
        our,
        competitor,
        subtitleSuffix: stateLine,
        higherIsBetter: false,
      });
    }
    return {
      title,
      value: String(our),
      subtitle: stateLine,
    };
  }

  return buildCompetitorKpiCard({ title, our, competitor });
}
